// Phone helpers. `normalizePhone` / `formatPhone` are a FE mirror of the api's
// `NormalizationUtil.normalizePhone` (copied 1:1 from the CHR-04 template
// branch). `findPhoneSegments` is CHR-06 (§2 / contract §D): it recognises
// phone numbers INSIDE the free-text Kontakt / Poznámka columns so the UI can
// render them as clickable runs.
//
// The CANONICAL form is E.164 (`+420602118344`) and is what travels over the
// wire, lands in the clipboard and backs a `tel:` href. The grouped rendering
// (`+420 602 118 344`) is a DISPLAY concern only: it never round-trips into a
// payload.

// Separators the api strips before validating. Deliberately an ALLOW-LIST —
// letters / `#` / `*` survive so a mistyped number FAILS the E.164 match
// instead of being silently swallowed into a plausible-looking one (same
// defensive stance the api documents on `normalizePhone`).
const SEPARATORS = /[\s()\-/.]/g;

// E.164: leading `+`, non-zero country digit, 8–15 digits total.
const E164_RE = /^\+[1-9]\d{7,14}$/;

// Bare national number: exactly 9 digits ⇒ CZ (`+420`). 10+ bare digits are
// REJECTED rather than guessed.
const CZ_NATIONAL_RE = /^\d{9}$/;

const CZ_PREFIX = '+420';

// Prefixes whose national part is a 9-digit 3-3-3 group (CZ + SK). Any other
// country code renders canonical — inventing a grouping we cannot verify
// would be worse than showing the stored value.
const GROUPED_PREFIXES = [CZ_PREFIX, '+421'] as const;

const GROUP = 3;

/**
 * Canonicalises a user-typed phone into E.164, mirroring the api rule:
 * strip `\s ( ) - / .` → `00…` becomes `+…` → a bare 9-digit number gets
 * `+420` → the result must match E.164.
 *
 * Returns `null` for BOTH "blank" and "not a phone number" — callers that
 * need to tell them apart check emptiness themselves first.
 */
export const normalizePhone = (raw: string | null | undefined): string | null => {
  if (raw === null || raw === undefined) return null;
  let value = raw.replace(SEPARATORS, '');
  if (value.length === 0) return null;
  if (value.startsWith('00')) value = `+${value.slice(2)}`;
  if (!value.startsWith('+')) {
    if (!CZ_NATIONAL_RE.test(value)) return null;
    value = `${CZ_PREFIX}${value}`;
  }
  return E164_RE.test(value) ? value : null;
};

/**
 * Display grouping for a canonical phone — `+420602118344` renders as
 * `+420 602 118 344`. Anything that is not a recognised 9-digit CZ/SK number
 * is returned verbatim; `null` renders as an empty string so the caller can
 * decide what the empty cell looks like.
 */
export const formatPhone = (phone: string | null | undefined): string => {
  if (phone === null || phone === undefined || phone.length === 0) return '';
  for (const prefix of GROUPED_PREFIXES) {
    if (!phone.startsWith(prefix)) continue;
    const national = phone.slice(prefix.length);
    if (!CZ_NATIONAL_RE.test(national)) continue;
    return [
      prefix,
      national.slice(0, GROUP),
      national.slice(GROUP, GROUP * 2),
      national.slice(GROUP * 2),
    ].join(' ');
  }
  return phone;
};

/** One run of free text; `phone` non-null ⇒ the run IS a phone number. */
export interface PhoneSegment {
  // The text exactly as it appears in the source string (never re-formatted —
  // the client's own spelling stays on screen).
  text: string;
  // Canonical `+420…` form: what a click copies and what backs `tel:`.
  phone: string | null;
}

// CHR-06 contract §D. Optional `+420` / `420` country prefix, then nine digits
// either contiguous or grouped 3-3-3 by SINGLE spaces.
//
// The national part must OPEN with a plausible CZ leading digit: 6/7 mobile,
// 2–5 landline, 9x (VoIP / service). That single class is what keeps bank
// account numbers (`123456789/0800`) and grouped amounts (`1 000 000 000`)
// out of the clipboard — both are nine digits in a shape the old pattern
// happily accepted.
//
// The capture group is the national part alone, so the canonical value is
// always rebuilt as `+420` + 9 digits (never re-parsed from the raw match).
const PHONE_RUN_RE =
  /(?:\+?420 ?)?([2-79]\d{2} \d{3} \d{3}|[2-79]\d{8})(?![\d]|[.,]\d)/g;

// Characters that, immediately BEFORE a run, disqualify it: another digit
// (the run is a slice of a longer number) or a decimal separator (the run is
// the fractional part of a GPS coordinate — `49.07960`, `50.123456789`).
// Checked in code rather than as a lookbehind, which Safari < 16.4 cannot
// parse at all (a `SyntaxError` there would take the whole module down).
const BLOCKING_PREFIX_RE = /[\d.,]/;

/**
 * Splits free text (Kontakt / Poznámka) into alternating plain and phone runs.
 * Concatenating `text` over the result reproduces the input exactly, so the
 * renderer can map segments 1:1 without losing a character.
 *
 * Blank input yields an empty array; text without a phone yields a single
 * plain segment.
 */
export const findPhoneSegments = (text: string): PhoneSegment[] => {
  if (text.length === 0) return [];
  const segments: PhoneSegment[] = [];
  const re = new RegExp(PHONE_RUN_RE.source, 'g');
  let cursor = 0;
  let match = re.exec(text);
  while (match !== null) {
    const national = match[1];
    // Group 1 is not optional, so it is always present; the check exists only
    // to narrow `string | undefined` under `noUncheckedIndexedAccess`.
    if (national === undefined) break;
    const before = match.index > 0 ? text.charAt(match.index - 1) : '';
    // A `+` immediately before a run whose own `+420` prefix was NOT consumed
    // means the run is the tail of a FOREIGN number (`+421 902 123 456`).
    // Re-badging it `+420…` would put a different subscriber's number on the
    // clipboard, so the whole run is skipped — not retried one char in, which
    // would just match the next 3-3-3 window inside the same number.
    const prefixConsumed = match[0].length > national.length;
    if (!prefixConsumed && before === '+') {
      re.lastIndex = match.index + match[0].length;
      match = re.exec(text);
      continue;
    }
    if (BLOCKING_PREFIX_RE.test(before)) {
      // Not a phone after all — resume ONE character in so a genuine number
      // further along the same digit run is still found.
      re.lastIndex = match.index + 1;
      match = re.exec(text);
      continue;
    }
    if (match.index > cursor) {
      segments.push({ text: text.slice(cursor, match.index), phone: null });
    }
    segments.push({
      text: match[0],
      phone: `${CZ_PREFIX}${national.replace(/ /g, '')}`,
    });
    cursor = match.index + match[0].length;
    match = re.exec(text);
  }
  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), phone: null });
  }
  return segments;
};
